/**
 * Audit Trail Event List — table with expand-in-place detail view
 *
 * sl-97oo.1.1: Informationsreiche Tabellenzeilen
 * sl-97oo.3.1: Expand-in-place Event Detail
 */
import { CachedUserAvatar } from '@affine/core/components/cached-user-avatar';
import { useI18n } from '@affine/i18n';
import { ToggleRightIcon } from '@blocksuite/icons/rc';
import { Fragment, useCallback, useState } from 'react';

import {
  SettingsCodeBlock,
  SettingsDataList,
  SettingsDataRow,
} from '../shared/settings-content';
import type { WorkspaceMemberOption } from './audit-filters';
import { resolveAuditPrincipalDisplay } from './audit-principal';
import * as styles from './event-list.css';
import type { AuditEvent } from './types';

const CATEGORY_COLORS: Record<string, string> = {
  auth: '#3B82F6',
  rbac: '#F97316',
  membership: '#22C55E',
  ai: '#A855F7',
  automation: '#06B6D4',
  workflow: '#0EA5E9',
  mail: '#10B981',
  data: '#6B7280',
  billing: '#EAB308',
  admin: '#EC4899',
  oauth: '#8B5CF6',
  platform: '#14B8A6',
  settings: '#64748B',
};

function formatRelativeTime(
  iso: string,
  t: ReturnType<typeof useI18n>
): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffMin < 1) {
    return t['com.streamlain.settings.workspace.audit-trail.time.just-now']();
  }
  if (diffMin < 60) {
    return t['com.streamlain.settings.workspace.audit-trail.time.minutes-ago']({
      count: String(diffMin),
    });
  }
  if (diffHour < 24) {
    return t['com.streamlain.settings.workspace.audit-trail.time.hours-ago']({
      count: String(diffHour),
    });
  }
  if (diffDay < 30) {
    return t['com.streamlain.settings.workspace.audit-trail.time.days-ago']({
      count: String(diffDay),
    });
  }
  return date.toLocaleDateString();
}

function formatAbsoluteTime(iso: string, includeSeconds = false): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  if (!includeSeconds) {
    return `${day}.${month}.${year}, ${hours}:${minutes}`;
  }
  const seconds = String(d.getSeconds()).padStart(2, '0');
  return `${day}.${month}.${year}, ${hours}:${minutes}:${seconds}`;
}

function resolveAuditLabel(
  t: ReturnType<typeof useI18n>,
  prefix: string,
  key: string
): string {
  const i18nKey =
    `com.streamlain.settings.workspace.audit-trail.${prefix}.${key}` as const;
  const resolved = t[i18nKey]?.();
  // i18next echoes the raw key when no translation exists, so treat a
  // self-equal result (plus literal key-miss) as "not translated".
  if (!resolved || resolved === i18nKey) return key;
  return resolved;
}

// ── Detail panel ────────────────────────────────────────────────────────

interface EventDetailProps {
  event: AuditEvent;
  members: WorkspaceMemberOption[];
  systemLabel: string;
  unknownUserLabel: string;
}

const EventDetail = ({
  event,
  members,
  systemLabel,
  unknownUserLabel,
}: EventDetailProps) => {
  const t = useI18n();
  const categoryLabel = resolveAuditLabel(t, 'category', event.category);
  const actionLabel = resolveAuditLabel(t, 'action', event.action);
  const user = resolveAuditPrincipalDisplay(
    event,
    members,
    systemLabel,
    unknownUserLabel
  );

  const hasDetails =
    event.details !== null &&
    event.details !== undefined &&
    Object.keys(event.details as Record<string, unknown>).length > 0;

  return (
    <SettingsDataList data-testid="audit-event-detail">
      <SettingsDataRow
        label={t['com.streamlain.settings.workspace.audit-trail.column.time']()}
        value={formatAbsoluteTime(event.createdAt, true)}
      />
      <SettingsDataRow
        label={t[
          'com.streamlain.settings.workspace.audit-trail.column.category'
        ]()}
        value={categoryLabel}
      />
      <SettingsDataRow
        label={t[
          'com.streamlain.settings.workspace.audit-trail.column.action'
        ]()}
        value={actionLabel}
      />
      <SettingsDataRow
        label={t['com.streamlain.settings.workspace.audit-trail.column.user']()}
        value={user.title ? `${user.label} (${user.title})` : user.label}
      />
      {event.targetType && (
        <SettingsDataRow
          label={t[
            'com.streamlain.settings.workspace.audit-trail.detail.target-type'
          ]()}
          value={event.targetType}
        />
      )}
      {event.targetName && (
        <SettingsDataRow
          label={t[
            'com.streamlain.settings.workspace.audit-trail.column.target'
          ]()}
          value={event.targetName}
        />
      )}
      {event.targetId && (
        <SettingsDataRow
          label={t[
            'com.streamlain.settings.workspace.audit-trail.detail.target-id'
          ]()}
          value={event.targetId}
        />
      )}
      {event.ipAddress && (
        <SettingsDataRow
          label={t['com.streamlain.settings.workspace.audit-trail.detail.ip']()}
          value={event.ipAddress}
        />
      )}
      {hasDetails && (
        <SettingsDataRow
          label={t[
            'com.streamlain.settings.workspace.audit-trail.detail.details'
          ]()}
          value={
            <SettingsCodeBlock as="pre">
              {JSON.stringify(event.details, null, 2)}
            </SettingsCodeBlock>
          }
        />
      )}
    </SettingsDataList>
  );
};

// ── Event list table ────────────────────────────────────────────────────

interface EventListProps {
  events: AuditEvent[];
  members: WorkspaceMemberOption[];
}

export const EventList = ({ events, members }: EventListProps) => {
  const t = useI18n();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const systemUserLabel =
    t['com.streamlain.settings.workspace.audit-trail.user.system']();
  const unknownUserLabel =
    t['com.streamlain.settings.workspace.audit-trail.user.unknown']();

  const toggleExpand = useCallback((id: string) => {
    setExpandedId(prev => (prev === id ? null : id));
  }, []);

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table} role="table">
        <thead className={styles.thead}>
          <tr>
            <th className={`${styles.th} ${styles.colTime}`}>
              {t['com.streamlain.settings.workspace.audit-trail.column.time']()}
            </th>
            <th className={`${styles.th} ${styles.colCategory}`}>
              {t[
                'com.streamlain.settings.workspace.audit-trail.column.category'
              ]()}
            </th>
            <th className={`${styles.th} ${styles.colAction}`}>
              {t[
                'com.streamlain.settings.workspace.audit-trail.column.action'
              ]()}
            </th>
            <th className={`${styles.th} ${styles.colUser}`}>
              {t['com.streamlain.settings.workspace.audit-trail.column.user']()}
            </th>
            <th className={`${styles.th} ${styles.colTarget}`}>
              {t[
                'com.streamlain.settings.workspace.audit-trail.column.target'
              ]()}
            </th>
          </tr>
        </thead>
        <tbody>
          {events.map(event => {
            const isExpanded = expandedId === event.id;
            const categoryColor = CATEGORY_COLORS[event.category] ?? '#6B7280';
            const categoryLabel = resolveAuditLabel(
              t,
              'category',
              event.category
            );
            const actionLabel = resolveAuditLabel(t, 'action', event.action);
            const user = resolveAuditPrincipalDisplay(
              event,
              members,
              systemUserLabel,
              unknownUserLabel
            );
            const relativeTime = formatRelativeTime(event.createdAt, t);
            const absoluteDate = formatAbsoluteTime(event.createdAt);

            return (
              <Fragment key={event.id}>
                <tr
                  className={styles.tr}
                  data-expanded={isExpanded}
                  data-testid="audit-event-row"
                  onClick={() => toggleExpand(event.id)}
                >
                  <td className={styles.td}>
                    <div className={styles.timeCellLayout}>
                      <span
                        className={styles.expandIcon}
                        data-expanded={isExpanded}
                      >
                        <ToggleRightIcon width={14} height={14} />
                      </span>
                      <div className={styles.timeStack}>
                        <span className={styles.timeAbsolute}>
                          {absoluteDate}
                        </span>
                        <span className={styles.timeRelative}>
                          {relativeTime}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className={styles.td}>
                    <span
                      className={styles.categoryBadge}
                      style={{ backgroundColor: categoryColor }}
                    >
                      {categoryLabel}
                    </span>
                  </td>
                  <td className={styles.td}>
                    <span className={styles.actionText}>{actionLabel}</span>
                  </td>
                  <td className={styles.td}>
                    <div className={styles.userCell} title={user.title}>
                      <span className={styles.userAvatarWrapper}>
                        <CachedUserAvatar
                          userId={user.userId}
                          name={user.avatarName}
                          size={20}
                          url={user.avatarUrl}
                        />
                      </span>
                      <span className={styles.userEmail}>{user.label}</span>
                    </div>
                  </td>
                  <td className={styles.td}>
                    <span
                      className={styles.targetText}
                      title={event.targetName ?? undefined}
                    >
                      {event.targetName ?? '—'}
                    </span>
                  </td>
                </tr>
                {isExpanded && (
                  <tr key={`${event.id}-detail`} className={styles.detailRow}>
                    <td className={styles.detailCell} colSpan={5}>
                      <EventDetail
                        event={event}
                        members={members}
                        systemLabel={systemUserLabel}
                        unknownUserLabel={unknownUserLabel}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
